export interface EncryptedData {
  iv: string;
  data: string;
}

/**
 * Base class providing AES-GCM encryption utilities using Web Crypto API.
 * Uses PBKDF2 for key derivation with configurable iterations.
 *
 * Subclasses must call `initializeCrypto()` to derive the encryption key
 * before using `encrypt()` or `decrypt()` methods.
 */
export abstract class BaseEncryptedStorage {
  protected readonly ALGORITHM = 'AES-GCM';
  protected readonly KEY_LENGTH = 256;
  protected readonly ITERATIONS = 100000;

  protected cryptoKey: CryptoKey | null = null;

  /**
   * Initialize the crypto key from a password and salt.
   * Must be called before encrypt/decrypt operations.
   */
  protected async initializeCrypto(password: string, salt: Uint8Array): Promise<void> {
    this.cryptoKey = await this.deriveKey(password, salt);
  }

  /**
   * Check if the crypto key has been initialized.
   */
  protected isCryptoReady(): boolean {
    return this.cryptoKey !== null;
  }

  /**
   * Clear the crypto key from memory.
   */
  protected clearCryptoKey(): void {
    this.cryptoKey = null;
  }

  /**
   * Derive an AES-GCM key from a password using PBKDF2.
   */
  protected async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.ITERATIONS,
        hash: 'SHA-256',
      } as Pbkdf2Params,
      keyMaterial,
      { name: this.ALGORITHM, length: this.KEY_LENGTH } as AesKeyGenParams,
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt a string using AES-GCM with a random IV.
   */
  protected async encrypt(data: string): Promise<EncryptedData> {
    if (!this.cryptoKey) {
      throw new Error('Crypto key not initialized. Call initializeCrypto() first.');
    }

    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { name: this.ALGORITHM, iv } as AesGcmParams,
      this.cryptoKey,
      encoder.encode(data)
    );

    return {
      iv: this.bytesToBase64(iv),
      data: this.bytesToBase64(new Uint8Array(encrypted)),
    };
  }

  /**
   * Decrypt data that was encrypted with `encrypt()`.
   */
  protected async decrypt(encryptedData: EncryptedData): Promise<string> {
    if (!this.cryptoKey) {
      throw new Error('Crypto key not initialized. Call initializeCrypto() first.');
    }

    const decoder = new TextDecoder();
    const iv = this.base64ToBytes(encryptedData.iv);
    const data = this.base64ToBytes(encryptedData.data);

    const decrypted = await crypto.subtle.decrypt(
      { name: this.ALGORITHM, iv } as AesGcmParams,
      this.cryptoKey,
      data as BufferSource
    );

    return decoder.decode(decrypted);
  }

  /**
   * Convert a Uint8Array to a base64 string.
   * Processes in chunks to avoid call stack overflow with large arrays.
   */
  protected bytesToBase64(bytes: Uint8Array): string {
    const chunkSize = 0x8000; // 32KB chunks
    const chunks: string[] = [];

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      chunks.push(String.fromCodePoint(...chunk));
    }

    return btoa(chunks.join(''));
  }

  /**
   * Convert a base64 string to a Uint8Array.
   */
  protected base64ToBytes(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Get or create a salt for key derivation.
   * @param saltKey - The localStorage key to store the salt under
   */
  protected getOrCreateSalt(saltKey: string): Uint8Array {
    const existingSalt = localStorage.getItem(saltKey);
    if (existingSalt) {
      return this.base64ToBytes(existingSalt);
    }

    const salt = crypto.getRandomValues(new Uint8Array(16));
    localStorage.setItem(saltKey, this.bytesToBase64(salt));
    return salt;
  }
}
